import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Download, Edit, ArrowLeft, Trash2, Package } from "lucide-react";
import { ItemReceiptModal } from "../components/ItemReceiptModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format date to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

export default function PODetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  useEffect(() => {
    fetchPO();
  }, [id]);

  const fetchPO = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPO(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch PO:", err);
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/purchase-orders/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Auto-download PDF when status is changed to 'submitted'
      if (newStatus === 'submitted') {
        await downloadPDF();
      }
      
      fetchPO();
    } catch (err) {
      console.error("Failed to update status:", err);
      alert("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const confirmMaterialReceipt = async (receiptData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/purchase-orders/${id}/confirm-item-receipt`,
        receiptData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`✓ Receipt confirmed! ${response.data.message}\nReceived: ${response.data.quantity_received}\nPending: ${response.data.pending}`);
      setShowReceiptModal(false);
      setSelectedItem(null);
      setSelectedItemIndex(null);
      fetchPO();
    } catch (err) {
      console.error("Failed to confirm material receipt:", err);
      alert(err.response?.data?.detail || "Failed to confirm material receipt");
    }
  };

  const openReceiptModal = (item, index) => {
    setSelectedItem(item);
    setSelectedItemIndex(index);
    setShowReceiptModal(true);
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${po.po_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download PDF:", err);
      alert("Failed to download PDF");
    }
  };

  const deletePO = async () => {
    if (!window.confirm(`Are you sure you want to delete ${po.po_number}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/purchase-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/purchase-orders');
    } catch (err) {
      console.error("Failed to delete PO:", err);
      alert("Failed to delete purchase order");
    }
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`status-badge status-${status}`} data-testid={`status-${status}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-8">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Purchase order not found</p>
          <Link to="/purchase-orders" className="text-primary hover:underline">
            Back to Purchase Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <Link 
          to="/purchase-orders" 
          className="flex items-center gap-2 text-sm text-primary hover:underline mb-4"
          data-testid="back-to-pos"
        >
          <ArrowLeft size={16} />
          Back to Purchase Orders
        </Link>
      </div>

      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl tracking-tight text-primary mb-2" data-testid="po-number">
            {po.po_number}
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            {getStatusBadge(po.status)}
            {po.locked && (
              <span className="px-2 py-1 text-xs font-semibold rounded bg-amber-100 text-amber-800 border border-amber-300">
                🔒 LOCKED
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              Created on {new Date(po.created_at).toLocaleDateString()} by {po.created_by}
            </span>
          </div>
          {po.locked && (
            <p className="text-xs text-amber-700 mt-2">
              ⚠️ This PO has been submitted and is locked. Only admin can make changes.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={downloadPDF}
            data-testid="download-pdf-button"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-sm hover:bg-primary/90"
          >
            <Download size={16} />
            Download PDF
          </button>
          <Link to={`/purchase-orders/${id}/edit`}>
            <button 
              data-testid="edit-po-button"
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground text-sm font-medium rounded-sm hover:bg-secondary/80"
            >
              <Edit size={16} />
              Edit
            </button>
          </Link>
          <button
            onClick={deletePO}
            data-testid="delete-po-button"
            className="flex items-center gap-2 px-4 py-2 bg-destructive/10 text-destructive text-sm font-medium rounded-sm hover:bg-destructive/20"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>

      {/* Eurospa Company Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-sm p-6 mb-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-black uppercase mb-1" data-testid="company-name">EUROSPA TERRY TOWELS PVT LTD</h2>
          <p className="text-sm text-green-800">Plot no- 481 Phase 2 Barhi industrial area gannaur-131101</p>
          <div className="flex justify-center gap-6 mt-2 text-sm text-green-700">
            <span>📞 8950010017</span>
            <span>📞 8950020017</span>
          </div>
          <div className="flex justify-center gap-6 mt-1 text-sm text-green-700">
            <span>📧 info@eurospaterrytowels.com</span>
            <span>📧 jitesh@eurospaterrytowels.com</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-card border border-border rounded-sm p-6">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
            Vendor Information
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Vendor Name</p>
              <p className="font-medium" data-testid="vendor-name">{po.vendor_name}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Vendor Address</p>
              <p className="text-sm" data-testid="shipping-address">{po.shipping_address}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-6">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
            Order Details
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Delivery Date</p>
              <p className="font-medium" data-testid="delivery-date">{formatDate(po.delivery_date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Payment Terms</p>
              <p className="font-medium" data-testid="payment-terms">{po.payment_terms}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-6">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
            Status Management
          </h3>
          <div className="space-y-2 mb-4">
            {['draft', 'sent', 'received', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => updateStatus(status)}
                disabled={updating || po.status === status}
                data-testid={`status-button-${status}`}
                className={`w-full px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                  po.status === status
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Mark as {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          
          <div className="pt-4 border-t border-border">
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
              Receipt Summary
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Items:</span>
                <span className="font-mono font-medium">{po.items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Fully Received:</span>
                <span className="font-mono font-medium text-green-600">
                  {po.items.filter(item => (item.quantity_received || 0) >= item.quantity).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pending:</span>
                <span className="font-mono font-medium text-orange-600">
                  {po.items.filter(item => (item.quantity_received || 0) < item.quantity).length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm mb-6">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="font-heading font-semibold text-xl">Line Items & Material Receipt Status</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="items-table">
            <thead>
              <tr className="border-b border-border">
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Product
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Ordered (Qty)
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Received
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Pending
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Unit Price
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Tax Rate
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Total
                </th>
                <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {po.items.map((item, index) => {
                const quantityReceived = item.quantity_received || 0;
                const pendingQty = item.quantity - quantityReceived;
                const isFullyReceived = quantityReceived >= item.quantity;
                const isOverReceived = quantityReceived > item.quantity;
                const maxAllowed = item.quantity * 1.05;
                const canReceiveMore = quantityReceived < maxAllowed;
                
                return (
                  <tr key={index} className="border-b border-border" data-testid={`item-row-${index}`}>
                    <td className="px-6 py-4 text-sm">{index + 1}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{item.product_name}</span>
                        {isFullyReceived && !isOverReceived && (
                          <span className="px-2 py-0.5 text-xs font-bold bg-green-100 text-green-700 rounded-full">
                            Complete
                          </span>
                        )}
                        {isOverReceived && (
                          <span className="px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 rounded-full" data-testid={`extra-badge-${index}`}>
                            +{(quantityReceived - item.quantity).toFixed(1)} extra
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-mono">{item.quantity} {item.unit || 'pcs'}</td>
                    <td className={`px-6 py-4 text-sm text-right font-mono font-bold ${isOverReceived ? 'text-red-600' : 'text-green-600'}`}>
                      {quantityReceived}
                    </td>
                    <td className={`px-6 py-4 text-sm text-right font-mono font-bold ${pendingQty > 0 ? 'text-orange-600' : pendingQty < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {pendingQty > 0 ? pendingQty : pendingQty < 0 ? `${pendingQty} (extra)` : '0'}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-mono">₹{item.unit_price.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm text-right font-mono">{item.tax_rate}%</td>
                    <td className="px-6 py-4 text-sm text-right font-mono font-medium">
                      ₹{item.total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => openReceiptModal(item, index)}
                        data-testid={`receive-item-${index}`}
                        className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-sm hover:bg-primary/90"
                      >
                        Receive
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-border bg-muted/30">
          <div className="flex justify-end">
            <div className="w-full max-w-xs space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-mono" data-testid="subtotal">₹{po.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax (GST)</span>
                <span className="font-mono" data-testid="tax">₹{po.tax.toFixed(2)}</span>
              </div>
              <div className="h-px bg-border" />
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="font-mono text-primary" data-testid="total">₹{po.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {po.notes && (
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <h2 className="font-heading font-semibold text-xl mb-4">Notes</h2>
          <p className="text-sm whitespace-pre-wrap" data-testid="notes">{po.notes}</p>
        </div>
      )}

      {po.authorized_signatory && (
        <div className="bg-card border border-border rounded-sm p-6">
          <h2 className="font-heading font-semibold text-xl mb-4">Authorized Signatory</h2>
          <p className="text-sm font-medium" data-testid="authorized-signatory">{po.authorized_signatory}</p>
        </div>
      )}

      {showReceiptModal && selectedItem && (
        <ItemReceiptModal
          item={selectedItem}
          itemIndex={selectedItemIndex}
          poId={id}
          onClose={() => {
            setShowReceiptModal(false);
            setSelectedItem(null);
            setSelectedItemIndex(null);
          }}
          onConfirm={confirmMaterialReceipt}
        />
      )}
    </div>
  );
}
